import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  date_joined?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, email: string, password1: string, password2: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      try {
        // Validate that storedUser is valid JSON
        if (storedUser === 'undefined' || storedUser === 'null') {
          // Clear invalid data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setLoading(false);
          return;
        }
        
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        // Verify token is still valid
        fetchUser(storedToken);
      } catch (error) {
        // If JSON parsing fails, clear invalid data
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (authToken: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/user/`, {
        headers: {
          'Authorization': `Token ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        // Token is invalid, clear storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      // Handle network errors
      if (!response.ok && response.status === 0) {
        throw new Error('Network error. Please check your connection and try again.');
      }

      // Parse response - handle both JSON and potential errors
      let data;
      const text = await response.text();
      
      // Log raw response for debugging
      console.log('Login response status:', response.status);
      console.log('Login response text:', text);
      
      if (!text || text.trim() === '') {
        // Empty response but status is 200 - might be a configuration issue
        if (response.ok) {
          throw new Error('Server returned empty response. Please check server configuration.');
        } else {
          throw new Error('Empty response from server. Please try again.');
        }
      }
      
      try {
        data = JSON.parse(text);
        console.log('Login response data (parsed):', data);
      } catch (parseError) {
        console.error('Error parsing login response:', parseError);
        console.error('Response text that failed to parse:', text);
        throw new Error('Invalid response format from server. Please try again.');
      }

      if (!response.ok) {
        // Handle different error formats from dj-rest-auth
        let errorMessage = 'Login failed. Please check your credentials and try again.';
        
        if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors && data.non_field_errors.length > 0) {
          errorMessage = data.non_field_errors[0];
        } else if (data.username) {
          errorMessage = Array.isArray(data.username) ? data.username[0] : data.username;
        } else if (data.password) {
          errorMessage = Array.isArray(data.password) ? data.password[0] : data.password;
        }
        
        throw new Error(errorMessage);
      }

      // dj-rest-auth returns 'key' for token
      // Some configurations return 'user' as well, but if not, we'll fetch it separately
      const authToken = data.key || data.token;
      
      if (!authToken) {
        // Log the actual response for debugging
        console.error('Missing token in response');
        console.error('Response data:', data);
        console.error('Response status:', response.status);
        throw new Error(`Invalid response from server. Expected 'key' field, but got: ${JSON.stringify(Object.keys(data))}`);
      }
      
      // If user data is included, use it; otherwise fetch user data separately
      let userData = data.user;
      
      if (!userData) {
        // Fetch user data using the token
        try {
          const userResponse = await fetch(`${API_BASE_URL}/auth/user/`, {
            headers: {
              'Authorization': `Token ${authToken}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (userResponse.ok) {
            userData = await userResponse.json();
          } else {
            // If fetching user fails, we still have the token, so continue
            console.warn('Failed to fetch user data, but token is valid');
            // Create a minimal user object from what we know
            userData = { username: username }; // We at least know the username from login
          }
        } catch (fetchError) {
          console.error('Error fetching user data:', fetchError);
          // Create a minimal user object
          userData = { username: username };
        }
      }
      
      // Ensure userData is a valid object before stringifying
      if (typeof userData !== 'object' || userData === null) {
        throw new Error('Invalid user data received from server.');
      }
      
      setUser(userData);
      setToken(authToken);
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      // Re-throw to let the component handle it
      throw error;
    }
  };

  const signup = async (username: string, email: string, password1: string, password2: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/registration/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, email, password1, password2 }),
      });

      // Handle network errors
      if (!response.ok && response.status === 0) {
        throw new Error('Network error. Please check your connection and try again.');
      }

      // Handle 204 No Content response (common for registration when email verification is required)
      if (response.status === 204) {
        // Registration successful but no content returned
        // User needs to verify email or login separately
        throw new Error('Registration successful! Please check your email to verify your account, or try logging in.');
      }

      // Parse response - handle both JSON and potential errors
      let data;
      try {
        const text = await response.text();
        if (!text || text.trim() === '') {
          throw new Error('Empty response from server. Please try again.');
        }
        data = JSON.parse(text);
      } catch (parseError) {
        console.error('Error parsing signup response:', parseError);
        throw new Error('Invalid response format from server. Please try again.');
      }

      // Log response for debugging (remove in production)
      console.log('Signup response status:', response.status);
      console.log('Signup response data:', data);

      if (!response.ok) {
        // Handle validation errors - dj-rest-auth returns errors in different format
        let errorMessage = 'Sign up failed. Please check your information and try again.';
        
        // Check for field-specific errors
        if (data.username) {
          const usernameError = Array.isArray(data.username) ? data.username[0] : data.username;
          errorMessage = `Username: ${usernameError}`;
        } else if (data.email) {
          const emailError = Array.isArray(data.email) ? data.email[0] : data.email;
          errorMessage = `Email: ${emailError}`;
        } else if (data.password1) {
          const passwordError = Array.isArray(data.password1) ? data.password1[0] : data.password1;
          errorMessage = `Password: ${passwordError}`;
        } else if (data.password2) {
          const password2Error = Array.isArray(data.password2) ? data.password2[0] : data.password2;
          errorMessage = `Password confirmation: ${password2Error}`;
        } else if (data.non_field_errors && data.non_field_errors.length > 0) {
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else {
          // Try to get first error from any field
          const errorMessages = Object.values(data).flat() as string[];
          if (errorMessages.length > 0) {
            errorMessage = errorMessages[0];
          }
        }
        
        throw new Error(errorMessage);
      }

      // dj-rest-auth returns 'key' for token and 'user' for user data
      const userData = data.user;
      const authToken = data.key;
      
      if (!userData || !authToken) {
        throw new Error('Invalid response from server. Please try again.');
      }
      
      // Ensure userData is a valid object before stringifying
      if (typeof userData !== 'object' || userData === null) {
        throw new Error('Invalid user data received from server.');
      }
      
      setUser(userData);
      setToken(authToken);
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      // Re-throw to let the component handle it
      throw error;
    }
  };

  const logout = async () => {
    const authToken = localStorage.getItem('token');
    
    if (authToken) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.error('Error during logout:', error);
      }
    }
    
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        signup,
        logout,
        isAuthenticated: !!user && !!token,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
